import { Modal, ModalBody, ModalFooter } from 'react-bootstrap'
import React, { useReducer, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useImportCourseMutation } from '../../api/courses'
import { showErrorNotification } from '../../../shared/helpers'
import { WizardFooter } from './WizardFooter'
import { Step1CourseSearch } from './steps/Step1CourseSearch'
import { Step1Ngf } from './steps/Step1Ngf'
import { Step1WhsUk } from './steps/Step1WhsUk'
import { Step1GenericWhs } from './steps/Step1GenericWhs'
import { Step2CopyData } from './steps/Step2CopyData'
import { Step3MatchTees } from './steps/Step3MatchTees'
import { Step4AdditionalTees } from './steps/Step4AdditionalTees'
import {
  wizardReducer,
  INITIAL_STATE,
  ACTION_TYPES,
  SOURCE_CONFIGS,
  buildImportParams,
  getTotalSteps,
  getCanGoNext,
  getShowImportOnStep2,
} from './reducer'

export const ImportCourseModal = ({ show, setShow, source }) => {
  const config = SOURCE_CONFIGS[source]
  const ngfDefaultClubId = useSelector((state) => state?.misc?.data?.ngfDefaultClubId)
  const ngfTerminology = useSelector((state) => state?.misc?.data?.ngfTerminology || 'NGF')
  const genericWhsTerminology = useSelector((state) => state?.misc?.data?.genericWhsTerminology || 'WHS')
  const terminologyBySource = { ngf: ngfTerminology, generic_whs: genericWhsTerminology }

  const [wizardState, dispatch] = useReducer(wizardReducer, INITIAL_STATE)
  const {
    step,
    selectedCourseId, selectedCourseName, selectedTeeIds,
    selectedClubId, levelSelections, copyInfo,
    existingCourseId, archiveOldCourse, copyOptions,
    matchedTees, existingCourseTees, additionalTeeIds,
  } = wizardState

  const totalSteps = getTotalSteps(source, wizardState)
  const canGoNext = getCanGoNext(source, wizardState, ngfDefaultClubId)
  const showImportOnStep2 = getShowImportOnStep2(source, wizardState)

  const unmatchedTees = useMemo(() => {
    const matchedExistingIds = new Set(Object.values(matchedTees).map(String))
    return existingCourseTees.filter((t) => !matchedExistingIds.has(String(t.id)))
  }, [matchedTees, existingCourseTees])

  const handleClose = () => {
    dispatch({ type: ACTION_TYPES.RESET })
    setShow(false)
  }

  const handleNext = () => {
    if (step < totalSteps) dispatch({ type: ACTION_TYPES.NEXT_STEP })
  }

  const handleBack = () => {
    if (step > 1) dispatch({ type: ACTION_TYPES.PREV_STEP })
  }

  const [importCourse, { isLoading: isImporting }] = useImportCourseMutation()

  const handleImport = async () => {
    const params = buildImportParams(source, { ...wizardState, ngfDefaultClubId })
    try {
      const { redirect_url } = await importCourse(params).unwrap()
      window.location.href = redirect_url
    } catch {
      showErrorNotification(!window.I18n ? '' : window.I18n.t('courses.bootstrap_courses.import_failed'))
    }
  }

  const handleCourseSelect = useCallback(
    (courseId, courseName) => dispatch({ type: ACTION_TYPES.SELECT_COURSE, courseId, courseName }),
    [dispatch]
  )

  const handleTeesSelect = useCallback(
    (teeIds) => dispatch({ type: ACTION_TYPES.SET_SELECTED_TEE_IDS, teeIds }),
    [dispatch]
  )

  const handleLevelSelect = (level, value, _label, childLevels) =>
    dispatch({ type: ACTION_TYPES.SELECT_LEVEL, level, value, childLevels })

  const handleCopyOptionChange = (key, value) =>
    dispatch({ type: ACTION_TYPES.SET_COPY_OPTION, key, value })

  const handleTeeMatch = (crsId, existingId) =>
    dispatch({ type: ACTION_TYPES.MATCH_TEE, crsId, existingId })

  const handleAutoMatch = useCallback(
    (matches, existingCourseTees) =>
      dispatch({ type: ACTION_TYPES.APPLY_AUTO_MATCH, matches, existingCourseTees }),
    [dispatch]
  )

  const handleAdditionalTeeToggle = (teeId, checked) =>
    dispatch({ type: ACTION_TYPES.TOGGLE_ADDITIONAL_TEE, teeId, checked })

  const renderStep = () => {
    if (step === 1) {
      if (source === 'ngf') {
        return (
          <Step1Ngf
            selectedClubId={selectedClubId}
            onClubSelect={(clubId) => dispatch({ type: ACTION_TYPES.SELECT_CLUB, clubId })}
            selectedCourseId={selectedCourseId}
            onCourseSelect={handleCourseSelect}
          />
        )
      }
      if (source === 'whs_uk') {
        return <Step1WhsUk onCourseSelect={handleCourseSelect} />
      }
      if (source === 'generic_whs') {
        return (
          <Step1GenericWhs
            levelSelections={levelSelections}
            onLevelSelect={handleLevelSelect}
            onCourseSelect={handleCourseSelect}
          />
        )
      }
      return (
        <Step1CourseSearch
          source={source}
          onCourseSelect={handleCourseSelect}
          selectedCourseId={selectedCourseId}
          onTeesSelect={handleTeesSelect}
          selectedTeeIds={selectedTeeIds}
        />
      )
    }

    if (step === 2 && config.hasWizard) {
      return (
        <Step2CopyData
          copyInfo={copyInfo}
          onCopyInfoChange={(value) => dispatch({ type: ACTION_TYPES.SET_COPY_INFO, value })}
          existingCourseId={existingCourseId}
          onExistingCourseChange={(courseId) => dispatch({ type: ACTION_TYPES.SET_EXISTING_COURSE, courseId })}
          archiveOldCourse={archiveOldCourse}
          onArchiveOldCourseChange={(value) => dispatch({ type: ACTION_TYPES.SET_ARCHIVE_OLD_COURSE, value })}
          copyOptions={copyOptions}
          onCopyOptionChange={handleCopyOptionChange}
        />
      )
    }

    if (step === 3 && config.hasWizard) {
      return (
        <Step3MatchTees
          source={source}
          selectedCourseId={selectedCourseId}
          selectedCourseName={selectedCourseName}
          existingCourseId={existingCourseId}
          selectedTeeIds={selectedTeeIds}
          matchedTees={matchedTees}
          onTeeMatch={handleTeeMatch}
          onAutoMatch={handleAutoMatch}
        />
      )
    }

    if (step === 4 && config.hasWizard) {
      return (
        <Step4AdditionalTees
          source={source}
          unmatchedTees={unmatchedTees}
          additionalTeeIds={additionalTeeIds}
          onAdditionalTeeToggle={handleAdditionalTeeToggle}
        />
      )
    }

    return null
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="center-modal"
      backdrop="static"
    >
      <div className="courses-modal-title-container">
        <div className="courses-modal-title">
          {config.title(terminologyBySource[source])}
        </div>
      </div>
      <ModalBody className="courses-modal-body-container">
        {renderStep()}
      </ModalBody>
      <ModalFooter>
        <WizardFooter
          step={step}
          totalSteps={totalSteps}
          onCancel={handleClose}
          onBack={handleBack}
          onNext={handleNext}
          onImport={handleImport}
          canGoNext={canGoNext}
          showImportOnStep2={showImportOnStep2}
          isImporting={isImporting}
        />
      </ModalFooter>
    </Modal>
  )
}

ImportCourseModal.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  source: PropTypes.oneOf(['crs', 'scorecentre', 'golf_advisor', 'ngf', 'whs_uk', 'generic_whs']).isRequired,
}
